import apiClient from "@/services/api/apiClient"
export const PILOT_FRAMEWORKS = [
  "reach",
  "rohs",
  "prop65",
  "pop",
  "pfas",
  "tsca",
  "conflict_minerals",
  "other",
] as const
export type PilotFramework = (typeof PILOT_FRAMEWORKS)[number]
export interface PilotRequestInput {
  title: string
  scope: {
    issuerDomain: string
    requirements: { partNumber: string; frameworks: PilotFramework[] }[]
  }
}
export interface EvidenceMatch {
  candidateId: string
  sha256: string
  issuerDomain: string
  partNumber: string
  framework: PilotFramework
  decision: "supported" | "unresolved" | "excluded"
  sourceUrl: string
  quote: string
  page?: number | null
  scopeBasis: "exact_part" | "company_scope"
  scopeReviewed: boolean
  exclusionsReviewed: boolean
  matchMethod?: "manual" | "automatic_exact_pdf"
}
export interface AutoImportResult {
  revision: number
  nextCursor: string | null
  scanned: number
  matchedPairs: number
  uploaded: number
  reused: number
  notAutoMatched: number
  failed: string[]
}
export type ResearchOutcomeStatus =
  | "no_public_declaration"
  | "blocked"
  | "needs_research"
export interface OutcomeInput {
  expectedRevision: number
  partNumber: string
  framework: PilotFramework
  status: ResearchOutcomeStatus
  reason: string
  checkedUrl: string
}
export interface PilotRequest extends PilotRequestInput {
  id: string
  companyId: string
  createdBy: string
  revision: number
  matches: EvidenceMatch[]
  matchedBy: string | null
  outcomes: (Omit<OutcomeInput, "expectedRevision"> & {
    actor: string
    checkedAt: string
  })[]
}
export interface MatchedDeclaration {
  id: string
  fileName: string
  declarationNumber: string
  contentSha256: string
  processingStatus: string
  approvedAt: string | null
}
export interface ReleaseInput {
  recipientOrganization: string
  declarationIds: string[]
  expectedRevision: number
  publicSourcesConfirmed: true
}
async function downloadArchive(path: string, filename: string) {
  const { data } = await apiClient.get<Blob>(path, { responseType: "blob" })
  const url = URL.createObjectURL(data)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
export interface ResearchRelease {
  id: string
  recipientOrganization: string
  requestRevision: number
  items: { sha256: string; filename: string }[]
  deliveryReceipt: {
    actor: string
    recordedAt: string
    verification: "operator_attested"
    documents: { sha256: string; customerDeclarationReference: string }[]
  } | null
}
export const pilotRequests = {
  autoImport: async (
    id: string,
    body: { expectedRevision: number; cursor: string | null },
  ) =>
    (
      await apiClient.post<AutoImportResult>(
        `/company-index/research-requests/${id}/auto-import`,
        body,
      )
    ).data,
  releases: async (id: string, offset = 0) =>
    (
      await apiClient.get<ResearchRelease[]>(
        `/company-index/research-requests/${id}/releases`,
        { params: { offset, limit: 30 } },
      )
    ).data,
  recordDelivery: async (
    id: string,
    documents: { sha256: string; customerDeclarationReference: string }[],
  ) =>
    (
      await apiClient.post<ResearchRelease>(
        `/company-index/research-releases/${id}/delivery`,
        { uploadedToRecipient: true, documents },
      )
    ).data,

  saveOutcome: async (id: string, body: OutcomeInput) =>
    (
      await apiClient.post<PilotRequest>(
        `/company-index/research-requests/${id}/outcomes`,
        body,
      )
    ).data,
  all: async (offset = 0) =>
    (
      await apiClient.get<PilotRequest[]>("/company-index/research-requests", {
        params: { offset, limit: 30 },
      })
    ).data,
  declarations: async (id: string, offset = 0) =>
    (
      await apiClient.get<MatchedDeclaration[]>(
        `/company-index/research-requests/${id}/declarations`,
        { params: { offset, limit: 100 } },
      )
    ).data,
  prepareRelease: async (id: string, body: ReleaseInput) =>
    (
      await apiClient.post<{ id: string }>(
        `/company-index/research-requests/${id}/releases`,
        body,
      )
    ).data,
  downloadRelease: (id: string) =>
    downloadArchive(
      `/company-index/research-releases/${id}/download`,
      "approved-declarations.zip",
    ),
  internalBatches: async (id: string) =>
    (
      await apiClient.get<{
        revision: number
        batches: { index: number; files: number; bytes: number }[]
      }>(`/company-index/research-requests/${id}/internal-batches`)
    ).data,
  downloadInternal: (id: string, revision: number, batch: number) =>
    downloadArchive(
      `/company-index/research-requests/${id}/internal-package?expected_revision=${revision}&batch=${batch}`,
      "internal-review-documents.zip",
    ),

  saveMatches: async (
    requestId: string,
    expectedRevision: number,
    matches: EvidenceMatch[],
  ) =>
    (
      await apiClient.put<PilotRequest>(
        `/company-index/research-requests/${requestId}/matches`,
        { expectedRevision, matches },
      )
    ).data,

  list: async (companyId: string, offset = 0) =>
    (
      await apiClient.get<PilotRequest[]>(
        `/company-index/companies/${companyId}/research-requests`,
        { params: { offset, limit: 30 } },
      )
    ).data,
  create: async (companyId: string, body: PilotRequestInput) =>
    (
      await apiClient.post<PilotRequest>(
        `/company-index/companies/${companyId}/research-requests`,
        body,
      )
    ).data,
}
